import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import {
  TENANT_OPTIONS,
  TRANSACTION_ORIGIN_OPTIONS,
  TRANSACTION_STATUS_OPTIONS,
} from '../shared/display';
import { ContactSearchInput } from '@/shared/contact-search/ContactSearchInput';
import {
  EMPTY_SALES_FILTERS,
  type SalesTransactionFilters,
} from '../shared/sales-filters';

const BU_FILTER_OPTIONS = [
  { value: '', label: 'Todas as BUs' },
  ...TENANT_OPTIONS,
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  ...TRANSACTION_STATUS_OPTIONS,
];

const ORIGIN_FILTER_OPTIONS = [
  { value: '', label: 'Todas as origens' },
  ...TRANSACTION_ORIGIN_OPTIONS,
];

interface SalesFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: SalesTransactionFilters;
  onApply: (filters: SalesTransactionFilters) => void;
}

export function SalesFiltersModal({
  open,
  onClose,
  filters,
  onApply,
}: SalesFiltersModalProps) {
  const [draft, setDraft] = useState(filters);
  /**
   * O termo digitado na busca de contato vive aqui, e não dentro do
   * `ContactSearchInput`: o `Modal` desmonta os filhos ao fechar
   * (`if (!open) return null`), então estado interno lá seria perdido a cada
   * fechamento — enquanto os demais campos sobrevivem no `draft`.
   *
   * Diferente do `draft`, **não** é resetado ao reabrir: é texto de busca, não
   * valor de filtro (só a seleção vira filtro), então descartá-lo não é o que
   * "Cancelar" quer dizer — é só perder o que a pessoa digitou.
   */
  const [contactTerm, setContactTerm] = useState('');

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const update = <K extends keyof SalesTransactionFilters>(
    key: K,
    value: SalesTransactionFilters[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filtrar transações"
      description="Todos os filtros rodam no servidor, sobre a base inteira de transações."
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(EMPTY_SALES_FILTERS);
              setContactTerm('');
            }}
          >
            Limpar
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleApply}>
            Aplicar filtros
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <ContactSearchInput
          value={draft.contactUserId || null}
          onChange={(userId) =>
            setDraft((prev) => ({ ...prev, contactUserId: userId ?? '' }))
          }
          term={contactTerm}
          onTermChange={setContactTerm}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="ID externo (gateway)"
            placeholder="Filtrar por ID externo..."
            value={draft.externalId}
            onChange={(e) => update('externalId', e.target.value)}
          />
          <Select
            label="BU"
            id="sales-filter-bu"
            options={BU_FILTER_OPTIONS}
            value={draft.businessUnit}
            onChange={(e) =>
              update(
                'businessUnit',
                e.target.value as SalesTransactionFilters['businessUnit'],
              )
            }
          />
          <Select
            label="Status"
            id="sales-filter-status"
            options={STATUS_FILTER_OPTIONS}
            value={draft.status}
            onChange={(e) =>
              update(
                'status',
                e.target.value as SalesTransactionFilters['status'],
              )
            }
          />
          <Select
            label="Origem"
            id="sales-filter-origin"
            options={ORIGIN_FILTER_OPTIONS}
            value={draft.origin}
            onChange={(e) =>
              update(
                'origin',
                e.target.value as SalesTransactionFilters['origin'],
              )
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="ID externo da oferta (gateway)"
            placeholder="Filtrar por ID externo da oferta..."
            value={draft.offerExternalId}
            onChange={(e) => update('offerExternalId', e.target.value)}
          />
          <Input
            label="ID externo do produto (gateway)"
            placeholder="Filtrar por ID externo do produto..."
            value={draft.productExternalId}
            onChange={(e) => update('productExternalId', e.target.value)}
          />
        </div>

        <div>
          <p className="text-(--color-text-primary) mb-2 text-sm font-medium">
            Cobrada entre
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="De"
              type="date"
              value={draft.occurredFrom}
              onChange={(e) => update('occurredFrom', e.target.value)}
            />
            <Input
              label="Até"
              type="date"
              value={draft.occurredTo}
              onChange={(e) => update('occurredTo', e.target.value)}
            />
          </div>
        </div>

        <div>
          <p className="text-(--color-text-primary) mb-2 text-sm font-medium">
            Criada em
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="De"
              type="date"
              value={draft.createdFrom}
              onChange={(e) => update('createdFrom', e.target.value)}
            />
            <Input
              label="Até"
              type="date"
              value={draft.createdTo}
              onChange={(e) => update('createdTo', e.target.value)}
            />
          </div>
        </div>

        <div>
          <p className="text-(--color-text-primary) mb-2 text-sm font-medium">
            Última alteração
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="De"
              type="date"
              value={draft.updatedFrom}
              onChange={(e) => update('updatedFrom', e.target.value)}
            />
            <Input
              label="Até"
              type="date"
              value={draft.updatedTo}
              onChange={(e) => update('updatedTo', e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
